import React from "react";

/**
 * Compares a single metric against the client's own lifetime baseline.
 * Anchored on the client's *own* performance rather than absolute
 * industry thresholds, because "healthy CPM" varies wildly by region /
 * audience / objective and a hard number would lie.
 *
 * Hidden entirely when the baseline rests on fewer than 2 historical
 * campaigns — single-campaign baselines are noise.
 *
 * Used in two places:
 *   - The aggregate strip on /ads (one chip per cost/efficiency metric)
 *   - Per-row in the live campaigns table (one chip per cell)
 */

// Within ±10% reads as "on pace" — comparable, not noteworthy.
const HEALTHY_BAND = 0.1;
// Beyond 20% the wrong direction reads "needs attention".
const WORSE_THRESHOLD = 0.2;

export interface BaselineDeltaProps {
  current: number | null | undefined;
  baseline: number | null | undefined;
  sample: number;
  /** CTR-style metric where higher numbers are better. False for
   *  CPM / CPC where lower is the goal. */
  higherIsBetter: boolean;
  /** Optional prefix when the metric label above isn't the same as
   *  what we're comparing (e.g. Conversions tile compares CPC).
   *  Shown inline AND in the tooltip. */
  labelPrefix?: string;
  /** Compact mode drops the textual "on pace" / "vs avg" trailer for
   *  use in tight table cells. */
  compact?: boolean;
}

export const BaselineDelta: React.FC<BaselineDeltaProps> = ({
  current,
  baseline,
  sample,
  higherIsBetter,
  labelPrefix,
  compact,
}) => {
  if (
    current == null ||
    baseline == null ||
    baseline === 0 ||
    sample < 2
  ) {
    return null;
  }
  const ratio = current / baseline;
  const diff = ratio - 1;
  const absDiff = Math.abs(diff);
  const isFavorable = higherIsBetter ? diff > 0 : diff < 0;

  let tone: "good" | "warn" | "neutral" = "neutral";
  if (absDiff <= HEALTHY_BAND) {
    tone = "neutral";
  } else if (isFavorable) {
    tone = "good";
  } else if (absDiff >= WORSE_THRESHOLD) {
    tone = "warn";
  } else {
    tone = "neutral";
  }

  const pct = (absDiff * 100).toFixed(0);
  const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
  const toneClass = {
    good: "bg-emerald-50 text-emerald-700 ring-emerald-200/80",
    warn: "bg-red-50 text-red-700 ring-red-200/80",
    neutral: "bg-gray-50 text-gray-500 ring-gray-200/70",
  }[tone];
  const labelText = absDiff <= HEALTHY_BAND ? "on pace" : "vs avg";

  return (
    <span
      className={[
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ring-1",
        compact ? "text-[9px]" : "text-[10px]",
        "font-semibold tabular-nums",
        toneClass,
      ].join(" ")}
      title={
        labelPrefix
          ? `${labelPrefix}: ${arrow} ${pct}% vs client lifetime avg (${sample} past campaign${sample === 1 ? "" : "s"})`
          : `${arrow} ${pct}% vs client lifetime avg (${sample} past campaign${sample === 1 ? "" : "s"})`
      }
    >
      {labelPrefix && !compact && (
        <span className="font-normal opacity-70 mr-0.5">{labelPrefix}</span>
      )}
      {arrow} {pct}%
      {!compact && (
        <span className="font-normal opacity-70">{labelText}</span>
      )}
    </span>
  );
};
